import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { ServiceRow } from "@/components/ServiceRow";
import { SearchBar } from "@/pages/Home";
import { logUsage } from "@/lib/api";

const PAGE = 20;
const norm = (s) => (s || "").toLowerCase();

export default function ServicesPage() {
  const { t, catalog, secName, lang } = useApp();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const sector = params.get("sector") || "";
  const [persona, setPersona] = useState("");
  const [maxFee, setMaxFee] = useState("");
  const [maxSla, setMaxSla] = useState("");
  const [showFilters, setShowFilters] = useState(!!sector);
  const [limit, setLimit] = useState(PAGE);

  useEffect(() => { setLimit(PAGE); }, [q, sector, persona, maxFee, maxSla]);
  const set = (k, v) => { const p = new URLSearchParams(params); v ? p.set(k, v) : p.delete(k); setParams(p); };

  const list = useMemo(() => {
    const terms = norm(q).split(/\s+/).filter((x) => x.length > 1);
    return (catalog?.services || []).filter((s) => {
      if (sector && s.sector_id !== sector) return false;
      if (persona && !s.eligible_personas.includes(persona)) return false;
      if (maxFee !== "" && s.fee_qar > Number(maxFee)) return false;
      if (maxSla !== "" && s.sla_days > Number(maxSla)) return false;
      if (!terms.length) return true;
      const hay = `${norm(s.name_en)} ${s.name_ar} ${s.keywords_en} ${s.keywords_ar} ${norm(s.ministry_en)} ${s.service_id.toLowerCase()}`;
      return terms.every((x) => hay.includes(x));
    });
  }, [catalog, q, sector, persona, maxFee, maxSla]);

  const sel = "h-11 w-full rounded-xl border border-input bg-card px-3 text-sm";
  return (
    <div className="space-y-4 px-4 pt-4" data-testid="services-page">
      <h1 className="text-2xl font-bold">{t("allServices")}</h1>
      <SearchBar key={q} initial={q} onSubmit={(v) => { if (v.trim()) logUsage({ type: "search", screen: "catalog", value: v, lang, mode: "typed" }); set("q", v); }} />
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground" data-testid="services-result-count"><span className="font-bold text-foreground">{list.length}</span> {t("results")}</p>
        <button data-testid="services-filters-toggle" onClick={() => setShowFilters((x) => !x)} className="inline-flex min-h-[40px] items-center gap-1.5 rounded-full border px-3 text-sm font-semibold">
          <SlidersHorizontal size={15} /> {t("filters")}
        </button>
      </div>
      <div className="q1-scroll -mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
        <button onClick={() => set("sector", "")} data-testid="sector-chip-all" className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold ${!sector ? "border-primary bg-primary text-primary-foreground" : "bg-card"}`}>{t("anySector")}</button>
        {(catalog?.sectors || []).map((s) => (
          <button key={s.sector_id} onClick={() => set("sector", s.sector_id)} data-testid={`sector-chip-${s.sector_id}`}
            className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold ${sector === s.sector_id ? "border-primary bg-primary text-primary-foreground" : "bg-card"}`}>{secName(s)}</button>
        ))}
      </div>
      {showFilters && (
        <div className="q1-fade-up grid grid-cols-2 gap-3 rounded-2xl border bg-card p-3 shadow-sm" data-testid="services-filters-panel">
          <label className="col-span-2 text-xs font-semibold">{t("persona")}
            <select data-testid="filter-persona-select" value={persona} onChange={(e) => setPersona(e.target.value)} className={sel}>
              <option value="">{t("anyPersona")}</option>
              {(catalog?.personas || []).map((p) => <option key={p.persona} value={p.persona}>{p.persona}</option>)}
            </select>
          </label>
          <label className="text-xs font-semibold">{t("maxFee")}
            <input data-testid="filter-max-fee-input" type="number" min="0" value={maxFee} onChange={(e) => setMaxFee(e.target.value)} className={sel} />
          </label>
          <label className="text-xs font-semibold">{t("maxSla")}
            <input data-testid="filter-max-sla-input" type="number" min="0" value={maxSla} onChange={(e) => setMaxSla(e.target.value)} className={sel} />
          </label>
          {(persona || maxFee || maxSla) && (
            <button data-testid="filter-clear-btn" onClick={() => { setPersona(""); setMaxFee(""); setMaxSla(""); }} className="col-span-2 inline-flex items-center justify-center gap-1 text-xs font-semibold text-primary"><X size={13} /> Clear</button>
          )}
        </div>
      )}
      <div className="space-y-2" data-testid="services-list">
        {!catalog && Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-muted" />)}
        {list.slice(0, limit).map((s, i) => <ServiceRow key={s.service_id} s={s} index={i % PAGE} />)}
      </div>
      {list.length > limit && (
        <button data-testid="services-load-more-btn" onClick={() => setLimit((x) => x + PAGE)} className="w-full rounded-xl border bg-card py-3 text-sm font-semibold text-primary">
          {t("loadMore")} ({list.length - limit})
        </button>
      )}
    </div>
  );
}
